import { History } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import {
  useProductsForFilter,
  useSalesHistory,
  type SalesHistoryRow,
} from '../../../hooks/inventory/useInventoryDashboard';

interface SalesHistoryTableProps {
  className?: string;
  limit?: number;
}

function formatVariant(r: SalesHistoryRow) {
  return [r.product_name, r.size, r.color].filter(Boolean).join(' · ');
}

function formatTime(iso: string) {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatVariantOption(v: { sku: string; size: string | null; color: string | null }) {
  const parts = [v.size, v.color].filter(Boolean).join(' · ');
  return parts ? `${parts} (${v.sku})` : v.sku;
}

export function SalesHistoryTable({ className = '', limit = 50 }: SalesHistoryTableProps) {
  const [productId, setProductId] = useState<string>('');
  const [variantId, setVariantId] = useState<string>('');

  const products = useProductsForFilter();
  const history = useSalesHistory(limit, {
    productId: productId || undefined,
    variantId: variantId || undefined,
  });

  const variantsForProduct = useMemo(() => {
    if (!productId) return [];
    return products.data?.find((p) => p.id === productId)?.variants ?? [];
  }, [products.data, productId]);

  const data = history.data ?? [];
  const selectClass =
    'bg-[#363232] border border-[#504A4A] rounded-md px-2 py-1 text-sm text-text-primary disabled:opacity-50';

  return (
    <Card className={`bg-[#221F1F] border-[#504A4A] ${className}`}>
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <CardTitle className="text-text-primary flex items-center gap-2">
          <History className="w-4 h-4 text-text-secondary" />
          Sales history
        </CardTitle>
        <div className="flex gap-2">
          <select
            aria-label="Filter by product"
            className={selectClass}
            value={productId}
            onChange={(e) => {
              setProductId(e.target.value);
              setVariantId('');
            }}
          >
            <option value="">All products</option>
            {(products.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <select
            aria-label="Filter by variant"
            className={selectClass}
            value={variantId}
            onChange={(e) => setVariantId(e.target.value)}
            disabled={!productId || variantsForProduct.length === 0}
          >
            <option value="">All variants</option>
            {variantsForProduct.map((v) => (
              <option key={v.id} value={v.id}>
                {formatVariantOption(v)}
              </option>
            ))}
          </select>
        </div>
      </CardHeader>
      <CardContent>
        {data.length === 0 ? (
          <p className="text-text-secondary text-sm py-6 text-center">
            {productId || variantId ? 'No sales match this filter.' : 'No POS sales yet.'}
          </p>
        ) : (
          <div className="divide-y divide-[#363232]">
            <div className="grid grid-cols-[140px_1fr_60px] gap-3 pb-2 text-xs font-semibold text-white/60 uppercase">
              <div>When</div>
              <div>Variant</div>
              <div className="text-right">Qty</div>
            </div>
            {data.map((r) => (
              <div
                key={r.id}
                className="grid grid-cols-[140px_1fr_60px] gap-3 py-2 text-sm"
              >
                <div className="text-white/70 font-mono text-xs">{formatTime(r.created_at)}</div>
                <div className="min-w-0">
                  <div className="text-text-primary truncate">{formatVariant(r)}</div>
                  <div className="text-xs text-white/50 truncate">SKU {r.sku}</div>
                </div>
                <div className="text-right font-mono text-text-primary">{-r.quantity_delta}</div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
